import { isPlayerInRound } from "vue-chess-shared/tournament/utils/playerUtils";
import * as DateHelper from "vue-chess-shared/utils/date-helper";

export function createWithManager(tournamentManager) {
  return {
    data() {
      return {
        isLoading: true,
        roundLoadings: {},
      };
    },

    computed: {
      activePlayers() {
        return this.players.filter((p) => !p.deletedAt);
      },

      sortedPlayers() {
        const roundNumber = Math.min(
          this.tournament.currentRound,
          this.tournament.totalRounds
        );

        return this.activePlayers
          .map((p) => ({
            ...p,
            isParticipating: isPlayerInRound(p, roundNumber),
            seed:
              p.user && p.user.stats && p.user.stats.rating
                ? p.user.stats.rating
                : 0,
            games: this.games.filter(
              (g) =>
                g.whiteUsername === p.username || g.blackUsername === p.username
            ),
          }))
          .sort((a, b) => b.score - a.score || b.seed - a.seed);
      },

      sortedRounds() {
        if (!this.rounds) return [];
        return [...this.rounds].sort((a, b) => a.roundNumber - b.roundNumber);
      },
    },

    methods: {
      async scheduleRounds() {
        this.isLoading = true;
        await tournamentManager.scheduleRounds(this.tournamentId);
        this.isLoading = false;
      },

      async changeTournamentStartTime(date) {
        this.isLoading = true;
        await tournamentManager.updateStartTime(this.tournamentId, date);
        await tournamentManager.pushRounds(
          this.tournamentId,
          0,
          DateHelper.diffIn(date, this.sortedRounds[0].startTime, "minute")
        );
        this.isLoading = false;
      },

      async changeRoundStartTime(round, date) {
        this.roundLoadings[round.roundNumber] = true;
        date.setMilliseconds(0);
        await tournamentManager.updateRoundStartTime(
          this.tournamentId,
          round.roundNumber,
          date
        );
        this.roundLoadings[round.roundNumber] = false;
      },

      async pushRoundStartTime(round, minutes) {
        await tournamentManager.pushRounds(
          this.tournamentId,
          round.roundNumber,
          minutes
        );
      },

      async toggleRoundsParticipated({ player, roundNumber }) {
        this.roundLoadings[roundNumber] = true;
        if (player.isParticipating) {
          await tournamentManager.unparticipate(
            this.tournamentId,
            player.username,
            roundNumber
          );
        } else {
          await tournamentManager.participate(
            this.tournamentId,
            player.username,
            roundNumber
          );
        }
        this.roundLoadings[roundNumber] = false;
      },

      async startRound(round) {
        this.roundLoadings[round.roundNumber] = true;
        await tournamentManager.startRound(
          this.tournamentId,
          round.roundNumber
        );
        console.log(
          "TournamentManagerClientFactory startRound",
          round.roundNumber
        );
        this.$buefy.toast.open({
          message: `Round were started. Current round: ${round.roundNumber}`,
          type: "is-info",
        });
        this.roundLoadings[round.roundNumber] = false;
      },

      async abortRound(round) {
        this.roundLoadings[round.roundNumber] = true;
        await tournamentManager.abortRound(
          this.tournamentId,
          round.roundNumber
        );
        this.$buefy.toast.open({
          message: `Round ${round.roundNumber} games were aborted.`,
          type: "is-danger",
        });
        this.roundLoadings[round.roundNumber] = false;
      },

      async pairRound(round, callback) {
        console.log("pairRound", round);
        this.roundLoadings[round.roundNumber] = true;
        try {
          await tournamentManager.pairRound(
            this.tournamentId,
            round.roundNumber
          );
          this.roundLoadings[round.roundNumber] = false;

          // Call the callback with success
          if (callback) {
            callback(null, true);
          }
        } catch (error) {
          this.roundLoadings[round.roundNumber] = false;
          // Call the callback with error
          if (callback) {
            callback(error);
          } else {
            // Fallback error handling if no callback provided
            this.$buefy.toast.open({
              message: `Failed to pair round: ${error.message}`,
              type: "is-danger",
            });
          }
        }
      },

      async saveRound(round, roundGames, callback) {
        this.roundLoadings[round.roundNumber] = true;
        try {
          // First, save the round games
          await tournamentManager.saveRoundGames(
            this.tournament.id,
            roundGames
          );

          // After saving games, if the round has already finished, update standings
          // for all finished rounds starting from this one
          if (round.finishTime) {
            const fromRoundNumber = round.roundNumber;
            const finishedRounds = this.sortedRounds.filter(
              (r) => r.finishTime && r.roundNumber >= fromRoundNumber
            );

            // Update standings for each finished round starting from the saved round
            for (const finishedRound of finishedRounds) {
              await tournamentManager.updateStandings(
                this.tournament.id,
                finishedRound.roundNumber
              );
            }
          }

          this.roundLoadings[round.roundNumber] = false;

          // Call the callback with success if provided
          if (callback) {
            callback(null, true);
          }

          const standingsMessage = round.finishTime
            ? " Standings updated for finished rounds."
            : "";
          this.$buefy.toast.open({
            message: `Round games were saved successfully!${standingsMessage}`,
            type: "is-success",
          });
        } catch (error) {
          this.roundLoadings[round.roundNumber] = false;

          // Call the callback with error if provided
          if (callback) {
            callback(error);
          }

          this.$buefy.toast.open({
            message: `Failed to save round: ${error.message || error}`,
            type: "is-danger",
          });
        }
      },

      async startUnstartedGames(round) {
        this.roundLoadings[round.roundNumber] = true;
        await tournamentManager.startUnstartedRoundGames(
          this.tournament.id,
          round.roundNumber
        );
        this.roundLoadings[round.roundNumber] = false;
        this.$buefy.toast.open({
          message: "Round games were started successfully!",
          type: "is-success",
        });
      },

      async fixResults(round) {
        this.roundLoadings[round.roundNumber] = true;
        await tournamentManager.fixRound(this.tournamentId, round.roundNumber);
        this.$buefy.toast.open({
          message: `Round ${round.roundNumber}'s games were fixed.`,
          type: "is-info",
        });
        this.roundLoadings[round.roundNumber] = false;
      },

      async finishRound(round) {
        this.roundLoadings[round.roundNumber] = true;
        await tournamentManager.finishRound(
          this.tournamentId,
          round.roundNumber
        );
        this.$buefy.toast.open({
          message: `Round ${round.roundNumber} were finished.`,
          type: "is-info",
        });
        this.roundLoadings[round.roundNumber] = false;
      },

      async unfinishRound(round) {
        this.roundLoadings[round.roundNumber] = true;
        await tournamentManager.unfinishRound(
          this.tournamentId,
          round.roundNumber
        );
        this.$buefy.toast.open({
          message: `Round ${round.roundNumber} were unfinished.`,
          type: "is-info",
        });
        this.roundLoadings[round.roundNumber] = false;
      },

      async startGame() {
        // TODO: handle this for online tournaments
        // await saveRoundGame(
        //   firestore,
        //   this.tournament.id,
        //   this.round,
        //   game,
        //   this.roundGames
        // );
      },

      async rematchGame() {
        // TODO: handle this for online tournaments
        // await saveRoundGame(
        //   firestore,
        //   this.tournament.id,
        //   this.round,
        //   game,
        //   this.roundGames
        // );
      },
    },
  };
}
